import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'
import { BacklinkPanel } from '@/components/backlink-panel'
import { Panel } from '@/components/panel'
import { type BacklinkProfile, useBacklinkProfile } from '@/lib/backlinks'
import { formatCount, shortenUrl } from '@/lib/format'
import { useSiteScope } from '../../-section'
import { DomainFacet } from '../-facet'

export const Route = createFileRoute('/_app/$org/$host/backlinks/_list/')({
  component: SiteBacklinksPage,
})

/**
 * Who links to this site.
 *
 * The one section with no date picker: a domain either links to you or it does
 * not, and a seven-day window would not make that answer smaller in any way the
 * reader meant. What is dated is the change, and the two facets that are about
 * change are pages of their own with their window in the label.
 *
 * The series and the topline sit here rather than above the sidebar because
 * they are the state of the profile as it stands — the same reading the table
 * below them is a list of. A chart of the whole profile over a list of the
 * fourteen domains that went last month would be answering a question nobody
 * on that page asked.
 */
function SiteBacklinksPage() {
  const { org, host, site } = useSiteScope()
  const { data } = useBacklinkProfile(site)

  // The layout holds the section until the profile is ready, so by here it is.
  if (!data) return null

  return (
    <>
      <BacklinkPanel totals={data.current} previous={data.previous} series={data.series.monthly} />

      <DomainFacet
        title="Linking now"
        status="live"
        emptyLabel="No domain links to this site yet."
      />

      {data.pages.length > 0 && <LinkedPages data={data} org={org} host={host} />}
    </>
  )
}

/**
 * Which of the site's own urls the links reach. Only answers where link-level
 * data exists, so it is absent rather than empty for most sites — an empty
 * panel here would read as "nothing links to any page", which is the opposite
 * of what an unset switch means.
 */
function LinkedPages({ data, org, host }: { data: BacklinkProfile; org: string; host: string }) {
  return (
    <Panel title="Where the links point" flush>
      <ul className="divide-y divide-border">
        {data.pages.map((row) => (
          <li key={row.pageId}>
            <Link
              to="/$org/$host/pages/$pageId"
              params={{ org, host, pageId: String(row.pageId) }}
              className="group flex items-center gap-3 px-4 py-2.5 text-[12.5px] hover:bg-secondary/40"
            >
              <span className="min-w-0 flex-1 truncate font-mono" title={row.url}>
                {shortenUrl(row.url, host)}
              </span>
              <span className="shrink-0 font-mono text-[11px] text-muted-foreground tabular">
                {formatCount(row.domains)} domains
              </span>
              <ChevronRightIcon
                className="size-3.5 shrink-0 text-muted-foreground/40 transition-colors group-hover:text-plum"
                aria-hidden
              />
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  )
}
